import React, { useState, useMemo, useCallback } from "react"
import { IconCheck, Title, Menu } from "dash-styled"
import { useDispatch } from "react-redux"
import { Table, TableColumn } from "resha"
import { ReactComponent as IconClose } from "icons/lib/close-icon.svg"
import { ReactComponent as ReloadIcon } from "icons/lib/reload.svg"
import { ReactComponent as IconSpinner } from "icons/lib/spinner.svg"
import { TableHeader } from "../../../../../../../Common/Table/TableHeader"
import { turbine } from "neql"
import { useNeqlContextProvider } from "../../../../../../../../Hooks/useNeqlContextProvider"
import { Map } from "immutable"
import {
    failMessage,
    successMessage,
    warnMessage
} from "../../../../../../../../Actions/Client/MessagesActions"
import { Confirm } from "../../../../../../../Common/Modal/Confirm"

enum ExemptButton {
    unexemptAll = "Unexempt all",
    unexempt = "Unexempt selected",
    exempt = "Exempt selected",
    exemptAll = "Exempt all"
}

const whosQualifiedTableColumns: (
    onWorkerScoreRecompute: (workerUuid: string) => Promise<void>,
    workerScoreRecomputeMap: Map<string, true>
) => TableColumn<turbine.QualifiedWorker | null>[] = (
    onWorkerScoreRecompute,
    workerScoreRecomputeMap
) => [
    {
        className: "",
        renderHeader: () => "ID",
        render: column => <span className="font-medium">{column?.id}</span>
    },
    {
        className: "",
        renderHeader: () => "Email",
        render: column => <span className="font-medium">{column?.email}</span>
    },
    {
        className: "",
        renderHeader: () => "Attempts",
        render: column => (
            <span className="font-medium">{column?.attempts}</span>
        )
    },
    {
        className: "",
        renderHeader: () => "Score",
        render: column => <span className="font-medium">{column?.score}</span>
    },
    {
        className: "",
        renderHeader: () => "Exempt",
        render: column => (
            <span className="font-medium">{column?.exempted ? "Yes" : ""}</span>
        )
    },
    {
        className: "",
        renderHeader: () => "Qualified",
        render: column =>
            column?.qualified ? (
                <IconCheck size="lg" className="text-moss-600" />
            ) : (
                <IconClose className="mx-0.5 text-smoke-610" />
            )
    },
    {
        className: "",
        renderHeader: () => "Recompute",
        render: column => {
            if (column === null) {
                return null
            }
            const isRecomputing =
                !column?.uuid || workerScoreRecomputeMap.has(column.uuid)
            return (
                !column?.qualified && (
                    <button
                        className="p-2"
                        onClick={() =>
                            column?.uuid && onWorkerScoreRecompute(column.uuid)
                        }
                        disabled={isRecomputing}
                    >
                        {isRecomputing ? (
                            <IconSpinner className="w-5 h-5 text-smoke-610" />
                        ) : (
                            <ReloadIcon className="mx-0.5 text-smoke-610" />
                        )}
                    </button>
                )
            )
        }
    }
]

interface WhosQualifiedTableProps {
    testQualifier: turbine.TrainingTest | undefined
    workersData?: turbine.PaginatedQualifiedWorker | null
    reload: () => void
    loadMore?: () => Promise<unknown>
}

export const WhosQualifiedTable = ({
    testQualifier,
    workersData,
    reload,
    loadMore
}: WhosQualifiedTableProps) => {
    const [selection, setSelection] = useState<turbine.QualifiedWorker[]>([])
    const [workerScoreRecompute, setWorkerScoreRecompute] = useState<
        Map<string, true>
    >(Map<string, true>())
    const [actionDialog, setActionDialog] =
        useState<
            | {
                  title: string
                  description: string
                  yesLabel: string
                  action: () => void
              }
            | undefined
        >()
    const dispatch = useDispatch()
    const clientContext = useNeqlContextProvider("turbine")

    const [recomputeWorkerScore] = turbine.useRecomputeWorkerScoreMutation({
        client: clientContext
    })
    const [disqualifyWorkers, { loading: disqualifyWorkerLoading }] =
        turbine.useDisqualifyWorkersMutation({
            client: clientContext
        })
    const totalWorkers = workersData?.total ?? 0
    const workersList = useMemo(
        () => workersData?.content ?? [],
        [workersData?.content]
    )

    const onCloseActionDialog = useCallback(
        () => setActionDialog(undefined),
        []
    )
    const [exemptWorkerAll, { loading: exemptWorkerAllLoading }] =
        turbine.useExemptWorkerAllMutation({
            client: clientContext
        })

    const selectedIds = useMemo(() => {
        return selection.reduce<string[]>((acc, curr) => {
            if (curr.id === undefined || curr.id === null) {
                return acc
            }

            return [...acc, curr.id]
        }, [])
    }, [selection])

    const exemptButtonStatus = useMemo(() => {
        if (selection.length > 0) {
            const qualifiedCount = selection.reduce(
                (acc, curr) => (acc += curr.exempted ? 1 : 0),
                0
            )
            return qualifiedCount >= selection.length / 2
                ? ExemptButton.unexempt
                : ExemptButton.exempt
        }

        return workersList.every(worker => worker?.exempted)
            ? ExemptButton.unexemptAll
            : ExemptButton.exemptAll
    }, [selection, workersList])

    const disqualifyButtonStatus = useMemo(
        () => (selection.length > 0 ? "Disqualify selected" : "Disqualify all"),
        [selection]
    )

    const handleToggleRowCheck = useCallback(
        (worker: turbine.QualifiedWorker | null) => {
            if (worker === null) {
                return
            }
            const selectedWorkers = [...selection]
            const index = selection.indexOf(worker)

            if (index > -1) {
                selectedWorkers.splice(index, 1)
                setSelection(selectedWorkers)
            } else {
                setSelection([...selectedWorkers, worker])
            }
        },
        [selection]
    )

    const onExemptAction = useCallback(async () => {
        if (testQualifier?.trainingId) {
            setActionDialog(actionDialog =>
                actionDialog
                    ? {
                          ...actionDialog,
                          yesLabel:
                              exemptButtonStatus === ExemptButton.exemptAll ||
                              exemptButtonStatus === ExemptButton.exempt
                                  ? "Exempting..."
                                  : "Unexempting..."
                      }
                    : actionDialog
            )
            await exemptWorkerAll({
                variables: {
                    exemptWorkerAllTrainingId: testQualifier.trainingId,
                    exemptWorkerAllExempt:
                        exemptButtonStatus === ExemptButton.exemptAll ||
                        exemptButtonStatus === ExemptButton.exempt,
                    exemptWorkerAllWorkerUuids: selection
                        .filter(
                            ({ uuid }) => uuid !== undefined && uuid !== null
                        )
                        .map(({ uuid }) => uuid) as string[]
                }
            })

            reload()
            setSelection([])
            onCloseActionDialog()
        }
    }, [
        testQualifier,
        exemptWorkerAll,
        exemptButtonStatus,
        selection,
        reload,
        onCloseActionDialog
    ])

    const handleExemptionButtonClick = useCallback(
        () =>
            setActionDialog({
                title: `${exemptButtonStatus} workers`,
                description:
                    exemptButtonStatus === ExemptButton.exemptAll ||
                    exemptButtonStatus === ExemptButton.exempt
                        ? "Exempted workers can process datapoints without qualifying"
                        : "Unexempted workers need to qualify before processing datapoints",
                yesLabel: `${exemptButtonStatus}`,
                action: onExemptAction
            }),
        [exemptButtonStatus, onExemptAction]
    )

    const handleRecompute = useCallback(
        async (workerUuid: string) => {
            if (testQualifier?.trainingId) {
                if (!workerScoreRecompute.has(workerUuid)) {
                    setWorkerScoreRecompute(workerScoreRecompute =>
                        workerScoreRecompute.set(workerUuid, true)
                    )
                    try {
                        const recomputeWorkerScoreResult =
                            await recomputeWorkerScore({
                                variables: {
                                    workerUuid,
                                    trainingId: testQualifier.trainingId
                                }
                            })
                        switch (
                            recomputeWorkerScoreResult.data
                                ?.reComputeWorkerMetric
                        ) {
                            case "SUCCESS":
                                dispatch(
                                    successMessage("Score recompute success")
                                )
                                reload()
                                break
                            case "FAILED":
                                dispatch(failMessage("Score recompute failed"))
                                break
                            case "PENDING":
                                dispatch(
                                    warnMessage(
                                        "Worker has not completed the test"
                                    )
                                )
                                break
                            case "NO_ACTION":
                                break
                        }
                    } catch (e) {
                        dispatch(
                            failMessage("Failed to recompute worker score")
                        )
                    }
                    setWorkerScoreRecompute(workerScoreRecompute =>
                        workerScoreRecompute.remove(workerUuid)
                    )
                }
            }
        },
        [
            dispatch,
            recomputeWorkerScore,
            reload,
            testQualifier,
            workerScoreRecompute
        ]
    )

    const onDisqualifiyAction = useCallback(async () => {
        if (testQualifier?.trainingId) {
            setActionDialog(actionDialog =>
                actionDialog
                    ? { ...actionDialog, yesLabel: "Disqualifying..." }
                    : actionDialog
            )
            await disqualifyWorkers({
                variables: {
                    trainingId: testQualifier.trainingId,
                    workerUuids: selection
                        .filter(
                            ({ uuid, qualified }) =>
                                uuid !== undefined &&
                                uuid !== null &&
                                !!qualified
                        )
                        .map(({ uuid }) => uuid) as string[]
                }
            })

            reload()
            setSelection([])
            onCloseActionDialog()
        }
    }, [
        testQualifier,
        disqualifyWorkers,
        selection,
        reload,
        onCloseActionDialog
    ])

    const handleDisqualifiyButtonClick = useMemo(() => {
        if (testQualifier?.trainingId) {
            if (
                selection.length === 0 ||
                selection.filter(worker => !!worker.qualified).length > 0
            )
                return () =>
                    setActionDialog({
                        title: `${disqualifyButtonStatus} workers`,
                        description:
                            "Disqualified workers need to attempt again in order to qualify for processing datapoints",
                        yesLabel: `${disqualifyButtonStatus}`,
                        action: onDisqualifiyAction
                    })
        }
        return undefined
    }, [testQualifier, selection, disqualifyButtonStatus, onDisqualifiyAction])

    const qualifiedColumns = useMemo(
        () => whosQualifiedTableColumns(handleRecompute, workerScoreRecompute),
        [handleRecompute, workerScoreRecompute]
    )

    const extraActions = useMemo(() => {
        const actions = ([] as [string, (() => void) | undefined][]).concat(
            !exemptWorkerAllLoading
                ? [[exemptButtonStatus, handleExemptionButtonClick]]
                : [],
            handleDisqualifiyButtonClick
                ? [[disqualifyButtonStatus, handleDisqualifiyButtonClick]]
                : []
        )

        const handleExtraAction = (label: string) => {
            const action = actions.find(a => a[0] === label)

            if (action !== undefined) {
                action[1]?.()
            }
        }

        return (
            <Menu
                title="Actions"
                align="right"
                className="ml-2 z-20"
                disabled={actions.length === 0}
                items={actions.map(([label]) => label)}
                onChoose={handleExtraAction}
            />
        )
    }, [
        exemptWorkerAllLoading,
        exemptButtonStatus,
        handleExemptionButtonClick,
        handleDisqualifiyButtonClick,
        disqualifyButtonStatus
    ])

    return (
        <div>
            <div className="flex">
                <div className="flex-grow mb-2">
                    <Title
                        variant="xl"
                        weight="semibold"
                        className="text-rhino-900 mb-0"
                    >
                        Who’s qualified ({totalWorkers}{" "}
                        {totalWorkers === 1 ? "person" : "people"})
                    </Title>
                </div>
            </div>
            <div>
                <TableHeader
                    totalCount={totalWorkers ?? 0}
                    extraActions={extraActions}
                />
                <div className="flex-grow flex-shrink flex-basis-0 overflow-auto -m-6 mt-0 px-6">
                    <Table<turbine.QualifiedWorker | null>
                        columns={qualifiedColumns}
                        items={workersList}
                        itemCount={totalWorkers}
                        getKey={item => item?.id ?? ""}
                        checkedItemIds={selectedIds}
                        className="w-full h-0 min-h-0 mt-0"
                        rowClassName="mx-6"
                        loadMore={loadMore}
                        stickyHeader={true}
                        onToggleRowCheck={handleToggleRowCheck}
                    />
                </div>
            </div>
            {actionDialog && (
                <Confirm
                    title={actionDialog.title}
                    description={actionDialog.description}
                    yesLabel={actionDialog.yesLabel}
                    isProcessing={
                        exemptWorkerAllLoading || disqualifyWorkerLoading
                    }
                    onClickYes={actionDialog.action}
                    onClose={onCloseActionDialog}
                />
            )}
        </div>
    )
}
